// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — kingsGrace.js   ·   THE KING'S GRACE KIT  (v1.0.0)
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-16: "foil is just King's Grace." A Grace is the foil
// a COMPANION wears — the same finish a card wears in the Foil Vault,
// worn as a light around a body instead of a sheen on a face. Think the
// gold aura around Goku, kept tight to the model so a cramped screen
// never drowns in it.
//
// This file is the DATA and the WRITES. The pictures are GraceAura.jsx.
// Both are VENDORED BYTE-IDENTICAL into every title that shows a Grace
// (today: Terror, Annihilation; Rush is next). The master copy lives in
// gamemaster/engine/. Hash them before you trust them.
//
// ── THE SHAPE OF IT ─────────────────────────────────────────────────
//   · A Grace has a REALM — the game that sells it. Terror's ten are the
//     ten foils in its Foil Vault (same ids: holo_silver … holo_gauntlet),
//     so "every foil in the Vault exists for the companion" is true by
//     construction. Annihilation's four are its own, sold nowhere else.
//   · Ownership is (companion, grace, SOURCE). The same Grace bought at
//     the Vault and won in Story Mode are TWO records with TWO marks —
//     that is the whole point: the shop copy never erases the rarer one.
//   · Every record lives at profiles/{uid}/kingsGraces/{key}, which every
//     title already reads. Buy it in one game, wear it in all of them.
//   · The worn Grace rides the fields Terror always used for the old
//     Radiant sheen — equippedCompanionRadiant (the grace id) — plus
//     equippedCompanionGraceSource (which copy). Every surface that
//     already reads `radiantTier` keeps working with zero changes.
//   · Legacy: `unlockedRadiants` (the retired Chocolate-Coin Radiant
//     shop) is READ as owned, source `legacy_radiant`. Nothing is lost.
//
// ── THE LAWS IT OBEYS ───────────────────────────────────────────────
//   · Priced in ◆ Divine Crests, once per account per (companion, grace,
//     source) — the Foil Vault's Mythical Law, checked AT THE WRITE.
//   · You must qualify in the SELLING realm (registerGraceEligibility):
//     Terror — own the companion; Annihilation — be that being at that tier.
//   · Wearing a Grace never swaps your companion. Changing companion is
//     Terror's Sol Converter's job, and a Grace is not a way around it.
//   · Retired foils render nothing and cannot be bought (Doctrine §5).
//   · Append-only writes; runTransaction seals; no full-object set().
// ════════════════════════════════════════════════════════════════════

import { ref, get, update, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { spendCrests } from "./ieEconomy";
import { COMPANIONS, getCompanion, isCompanionUnlocked } from "./companions";

export const GRACE_KIT_VERSION = "1.0.0";

// ── THE REALMS ──────────────────────────────────────────────────────
export const REALMS = {
  terror:       { id: "terror",       label: "Terror" },
  annihilation: { id: "annihilation", label: "Annihilation" },
};

// ── THE GRACES ──────────────────────────────────────────────────────
//   crown   — always "King's Grace". caP'n, 2026-09-16: "let's just leave
//             them all as King's Grace, no Queen — raises questions as to
//             what separates them." (Canon may SAY Queen's Grace; the game
//             never draws a line between them.) Kept as a field so the
//             label lives in data, not in every renderer.
//   label   — the short name (matches the Vault's tier label for Terror)
//   title   — the motion's name, the same word the card foil uses
//   color / color2 / color3 — the light, lit edge, and deep tone
//   motion  — the recipe key in GraceAura.jsx. NO TWO GRACES SHARE ONE.
//   blurb   — the shelf line
// Terror's rows mirror PackArt.HOLO_TIER_INFO (label + colour). PackArt
// is not vendored, so the mirror is checked at runtime in Terror by
// graceDriftFrom(HOLO_TIER_INFO) — a drift prints, it never breaks.
export const GRACES = {
  // ══ TERROR — the ten foils of the Foil Vault ══════════════════════
  holo_silver:   { id: "holo_silver",   realm: "terror", crown: "King's Grace", label: "Silver",   title: "Quicksilver",
    color: "#cccccc", color2: "#f4f6fb", color3: "#5d6474", glyph: "◈", motion: "quicksilver",
    blurb: "Two liquid-metal bands climb and fall around it, never in step." },
  holo_gold:     { id: "holo_gold",     realm: "terror", crown: "King's Grace", label: "Gold",     title: "Molten",
    color: "#ffd700", color2: "#fff3b0", color3: "#ff8a1e", glyph: "✦", motion: "molten",
    blurb: "The standard gold. Tongues of molten light lick upward; flecks rise off it." },
  holo_mythic:   { id: "holo_mythic",   realm: "terror", crown: "King's Grace", label: "Mythic",   title: "Prism",
    color: "#ff66cc", color2: "#ffffff", color3: "#5b8cff", glyph: "◆", motion: "prism",
    blurb: "Three rings of red, green and blue drift apart and recombine into white." },
  holo_khao:     { id: "holo_khao",     realm: "terror", crown: "King's Grace", label: "Khao",     title: "Maelstrom",
    color: "#9333ea", color2: "#d8b4fe", color3: "#1e0536", glyph: "◈", motion: "maelstrom",
    blurb: "A throat of violet smoke turns at its feet and swallows the light." },
  holo_solrend:  { id: "holo_solrend",  realm: "terror", crown: "King's Grace", label: "Solrend",  title: "The Rend",
    color: "#ff2266", color2: "#fff0f4", color3: "#7a0022", glyph: "◆", motion: "rend",
    blurb: "Sunfire tears the air beside it, flares white, and seals. Embers climb." },
  holo_jynxi:    { id: "holo_jynxi",    realm: "terror", crown: "King's Grace",  label: "J'ynxi",  title: "The Jester",
    color: "#2ee66b", color2: "#c9ffd9", color3: "#0b5a2a", glyph: "✦", motion: "jester",
    blurb: "Emerald diamonds wheel both ways around it — and something up there grins." },
  holo_aeternus: { id: "holo_aeternus", realm: "terror", crown: "King's Grace", label: "A'eterni", title: "Halo",
    color: "#f0f0ff", color2: "#ffffff", color3: "#9aa0ff", glyph: "✦", motion: "halo",
    blurb: "Rings of light leave its feet one after another, so the pulse never stops." },
  holo_depths:   { id: "holo_depths",   realm: "terror", crown: "King's Grace", label: "Depths",   title: "Caustics",
    color: "#00aaff", color2: "#b8f0ff", color3: "#003a66", glyph: "◆", motion: "caustics",
    blurb: "The light-web of a surface far overhead dances over it; bubbles rise." },
  holo_vrown:    { id: "holo_vrown",    realm: "terror", crown: "King's Grace", label: "Vrown",    title: "The Griddle",
    color: "#c17a2e", color2: "#fff1c9", color3: "#5a2e0e", glyph: "◆", motion: "griddle",
    blurb: "A plate of white-gold under it sears to brown, and the shreds brown with it." },
  holo_gauntlet: { id: "holo_gauntlet", realm: "terror", crown: "King's Grace", label: "Gauntlet", title: "The Grip",
    color: "#b9c2cc", color2: "#eef3f8", color3: "#3c444e", glyph: "✦", motion: "grip",
    blurb: "Four plates close on it and clench — the only Grace that contracts." },

  // ══ ANNIHILATION — four of its own, sold only in the Old Fold ═════
  grace_edict:         { id: "grace_edict",         realm: "annihilation", crown: "King's Grace", label: "The Edict",        title: "Decree",
    color: "#e8a33d", color2: "#ffe6a8", color3: "#4a2a08", glyph: "⚜", motion: "edict",
    blurb: "Gilt runes of the law orbit its waist; every few breaths a seal stamps the ground." },
  grace_whisperleaf:   { id: "grace_whisperleaf",   realm: "annihilation", crown: "King's Grace",  label: "Whisperleaf",      title: "The Grove",
    color: "#d9a441", color2: "#b8e07a", color3: "#3a2a10", glyph: "❦", motion: "whisperleaf",
    blurb: "Ember-edged leaves of the Whispering Grove spiral up around it and never land." },
  grace_drowned_crown: { id: "grace_drowned_crown", realm: "annihilation", crown: "King's Grace",  label: "The Drowned Crown", title: "The Sunken Fold",
    color: "#2fb8a6", color2: "#c8fff6", color3: "#06221f", glyph: "♛", motion: "drowned",
    blurb: "A crown of standing water floats above it; drops fall from it into a tide at its feet." },
  grace_heretic_halo:  { id: "grace_heretic_halo",  realm: "annihilation", crown: "King's Grace", label: "Heretic's Halo",   title: "Heresy",
    color: "#d0224a", color2: "#ffb3c4", color3: "#2a0616", glyph: "✧", motion: "heresy",
    blurb: "A broken halo. Its shards pull apart, bleed violet, and snap back together." },
};

// Legacy keys (the old Radiant shop wrote these). Void IS Khao. Shadow is
// retired: it resolves, renders nothing, and is never sold.
export const GRACE_ALIAS = { holo_void: "holo_khao" };
export const RETIRED_GRACES = { holo_shadow: { label: "Shadow", color: "#9966ff" } };
export const canonGrace = (id) => GRACE_ALIAS[id] || id;
export const getGrace = (id) => GRACES[canonGrace(id)] || null;
export const isRetiredGrace = (id) => !!RETIRED_GRACES[canonGrace(id)];

export const TERROR_GRACE_ORDER = [
  "holo_silver", "holo_gold", "holo_mythic", "holo_khao", "holo_solrend",
  "holo_jynxi", "holo_aeternus", "holo_depths", "holo_vrown", "holo_gauntlet",
];
export const ANNIHILATION_GRACE_ORDER = [
  "grace_edict", "grace_whisperleaf", "grace_drowned_crown", "grace_heretic_halo",
];
export const GRACE_ORDER = [...TERROR_GRACE_ORDER, ...ANNIHILATION_GRACE_ORDER];
export const gracesOfRealm = (realm) => GRACE_ORDER.filter((g) => GRACES[g].realm === realm);

// ── THE SOURCES — where a copy came from. Each wears its own sigil. ──
//   rank   — how rare the copy reads; the Mark lists highest first.
//   sigil  — SourceSigil id (both titles carry the recipe).
export const GRACE_SOURCES = {
  vault:              { id: "vault",              game: "terror",       rank: 1, sigil: "grace_vault",        label: "Bought at the Foil Vault",  short: "Vault" },
  story:              { id: "story",              game: "terror",       rank: 3, sigil: "grace_story",        label: "Won in Story Mode",         short: "Story" },
  annihilation_stall: { id: "annihilation_stall", game: "annihilation", rank: 2, sigil: "grace_annihilation", label: "Bought in the Old Fold",    short: "Old Fold" },
  legacy_radiant:     { id: "legacy_radiant",     game: "terror",       rank: 0, sigil: "grace_vault",        label: "A Radiant Version, from the old shop", short: "Old Radiant" },
};
// Which source a realm's shop mints. One shop, one source.
export const SHOP_SOURCE_OF_REALM = { terror: "vault", annihilation: "annihilation_stall" };
export const getGraceSource = (id) => GRACE_SOURCES[id] || null;

// ── KEYS ────────────────────────────────────────────────────────────
// `~` joins, as the Vault's own `${cardId}~${tier}` does. Ids never
// contain `~`, `.`, `#`, `$`, `[`, `]` or `/`, so the key is a legal
// Firebase key as-is.
export const graceKey = (companionId, graceId, source) =>
  `${companionId}~${canonGrace(graceId)}~${source}`;
export const GRACE_ROOT = (uid) => `profiles/${uid}/kingsGraces`;
// How long a claimed-but-unpaid purchase is "in flight" before it is
// treated as owned (see buyGrace).
export const PENDING_MS = 60 * 1000;

// ── PRICES — each realm registers its own table ONCE; a caller never
// names a price. Terror registers FOIL_VAULT_PRICE (GraceVault.jsx),
// Annihilation its own stall table. An unregistered realm sells nothing.
const PRICE_OF_REALM = {};
export function registerGracePrices(realm, priceOf) {
  if (REALMS[realm] && typeof priceOf === "function") PRICE_OF_REALM[realm] = priceOf;
}
// ── WHO MAY BUY — each realm says what "yours" means ────────────────
// Terror: you own the companion. Annihilation: you ARE that being, at a
// tier you have reached (Annihilation's beingGrace.js registers that).
// A rule returns null when the buyer qualifies, or the reason they don't.
const ELIGIBLE_IN_REALM = {};
export function registerGraceEligibility(realm, rule) {
  if (REALMS[realm] && typeof rule === "function") ELIGIBLE_IN_REALM[realm] = rule;
}
function defaultEligibility(profile, companionId) {
  const c = getCompanion(companionId);
  return isCompanionUnlocked(profile, companionId) ? null : `You don't have ${c ? c.name : "that companion"} yet.`;
}
export function whyCannotBuy(realm, profile, companionId) {
  const rule = ELIGIBLE_IN_REALM[realm] || defaultEligibility;
  try { return rule(profile, companionId); } catch (_) { return "That Grace can't be bought here."; }
}

export function gracePriceIn(realm, graceId) {
  const f = PRICE_OF_REALM[realm];
  const n = f ? Number(f(canonGrace(graceId))) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

// ── READS (pure — take a profile snapshot, touch nothing) ───────────
/** Every Grace copy this profile holds, legacy Radiants included. */
export function ownedGraces(profile) {
  if (!profile) return [];
  const out = [];
  const seen = new Set();
  const push = (r) => {
    const k = graceKey(r.companionId, r.graceId, r.source);
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ ...r, graceId: canonGrace(r.graceId), key: k });
  };
  const rec = profile.kingsGraces || {};
  const now = Date.now();
  for (const v of Object.values(rec)) {
    if (!v || !v.companionId || !v.graceId || !v.source) continue;
    // A purchase in flight is not owned yet. A STALE one (the tab died
    // between paying and sealing) IS owned — the error favours the
    // player, because the Crests may already be gone.
    if (v.status === "pending" && now - (v.at || 0) < PENDING_MS) continue;
    if (!COMPANIONS[v.companionId]) continue;             // a removed companion
    if (!getGrace(v.graceId) && !isRetiredGrace(v.graceId)) continue;
    push({ companionId: v.companionId, graceId: v.graceId, source: v.source, at: v.at || 0 });
  }
  const legacy = profile.unlockedRadiants || {};
  for (const v of Object.values(legacy)) {
    if (!v || !v.companionId || !v.tier) continue;
    if (!COMPANIONS[v.companionId]) continue;
    if (!getGrace(v.tier) && !isRetiredGrace(v.tier)) continue;
    push({ companionId: v.companionId, graceId: v.tier, source: "legacy_radiant", at: v.at || 0 });
  }
  return out;
}

/** Copies for one companion, rarest source first. */
export function gracesFor(profile, companionId) {
  return ownedGraces(profile)
    .filter((g) => g.companionId === companionId)
    .sort((a, b) => (GRACE_SOURCES[b.source]?.rank || 0) - (GRACE_SOURCES[a.source]?.rank || 0));
}

/** Does the profile hold this Grace for this companion (any source, or one)? */
export function ownsGrace(profile, companionId, graceId, source = null) {
  const g = canonGrace(graceId);
  return ownedGraces(profile).some((r) =>
    r.companionId === companionId && r.graceId === g && (!source || r.source === source));
}

/** The copy a shop would sell — sold-out is per SOURCE, not per grace. */
export const isGraceSold = (profile, companionId, graceId, source) =>
  ownedGraces(profile).some((r) =>
    r.companionId === companionId && r.graceId === canonGrace(graceId) && r.source === source);

/**
 * What the equipped companion is wearing right now, validated.
 * Returns { companionId, graceId, source, grace } or null. A worn Grace
 * the profile does not actually hold (a hand-edited field, a companion
 * swapped after the Grace was worn) reads as NOTHING, never as the Grace.
 */
export function equippedGraceOf(profile) {
  if (!profile) return null;
  const companionId = profile.equippedCompanion || null;
  const graceId = profile.equippedCompanionRadiant ? canonGrace(profile.equippedCompanionRadiant) : null;
  if (!companionId || !graceId) return null;
  if (!getGrace(graceId)) return null;              // retired / unknown: wears nothing
  const copies = gracesFor(profile, companionId).filter((c) => c.graceId === graceId);
  if (!copies.length) return null;
  const want = profile.equippedCompanionGraceSource || null;
  const copy = copies.find((c) => c.source === want) || copies[0];
  return { companionId, graceId, source: copy.source, grace: getGrace(graceId) };
}

/** A render-safe grace id (null when retired or unknown). */
export const renderableGrace = (id) => {
  if (!id) return null;
  const c = canonGrace(id);
  return GRACES[c] ? c : null;
};

// ── THE MARK — what a companion's Mark should print ─────────────────
// { holoTiers: { graceId: n }, graces: [...copies], origins: [sigil…] }
export function graceMarkRecord(profile, companionId) {
  const copies = gracesFor(profile, companionId);
  if (!copies.length) return null;
  const holoTiers = {};
  const origins = [];
  for (const c of copies) {
    holoTiers[c.graceId] = (holoTiers[c.graceId] || 0) + 1;
    const s = GRACE_SOURCES[c.source]?.sigil;
    if (s && !origins.includes(s)) origins.push(s);
  }
  return { holoTiers, graces: copies, origins };
}

// ── DRIFT CHECK — Terror's PackArt is the card-side authority ───────
export function graceDriftFrom(holoTierInfo) {
  const drift = [];
  if (!holoTierInfo) return drift;
  for (const id of TERROR_GRACE_ORDER) {
    const a = holoTierInfo[id], b = GRACES[id];
    if (!a) { drift.push(`${id}: missing from HOLO_TIER_INFO`); continue; }
    if (a.label !== b.label) drift.push(`${id}: label ${a.label} ≠ ${b.label}`);
    if ((a.color || "").toLowerCase() !== b.color.toLowerCase()) drift.push(`${id}: color ${a.color} ≠ ${b.color}`);
  }
  return drift;
}

// ── WRITES ──────────────────────────────────────────────────────────
const fail = (reason, extra = {}) => ({ success: false, reason, ...extra });

// THE UID GUARD — a display name must never become a profile path.
// Firebase Auth uids are 28 url-safe characters; anything shorter, or
// with a space, a dot or a slash in it, is refused before any read.
// Same shape progression.js's _looksLikeUid has always enforced.
export const looksLikeUid = (k) => typeof k === "string" && /^[A-Za-z0-9]{20,40}$/.test(k);
const histKey = (at) => `${at}_${Math.floor(Math.random() * 1e9).toString(36)}`;

async function readProfile(uid) {
  const snap = await get(ref(db, `profiles/${uid}`));
  return snap.exists() ? snap.val() : null;
}

/**
 * Buy a Grace at a realm's shop. The shop decides the realm; the realm
 * decides the source AND the price (registerGracePrices).
 *   → { success, reason?, key?, alreadyOwned?, cost? }
 *
 * ORDER: CLAIM → PAY → SEAL. The slot is claimed first in a transaction
 * (status "pending"), so two tabs — or Terror and Annihilation open side
 * by side — cannot both pay for one copy: the second claim finds the
 * first and stops before any money moves. If the payment is refused the
 * claim is released. If the tab dies after paying and before sealing,
 * the pending record goes stale and is read as OWNED (ownedGraces).
 */
export async function buyGrace(uid, companionId, graceId, realm) {
  if (!looksLikeUid(uid)) return fail("Sign in first.");
  const g = getGrace(graceId);
  if (!g || isRetiredGrace(graceId)) return fail("That Grace is not sold.");
  if (!REALMS[realm]) return fail("Unknown shop.");
  if (g.realm !== realm) return fail(`${g.label} is only sold in ${REALMS[g.realm].label}.`);
  const comp = getCompanion(companionId);
  if (!comp) return fail("Unknown companion.");
  const cost = gracePriceIn(realm, g.id);
  if (cost == null) return fail("That Grace has no price yet.");
  const source = SHOP_SOURCE_OF_REALM[realm];

  const profile = await readProfile(uid);
  if (!profile) return fail("No profile yet.");
  const why = whyCannotBuy(realm, profile, companionId);
  if (why) return fail(why);

  const key = graceKey(companionId, g.id, source);
  const slot = ref(db, `${GRACE_ROOT(uid)}/${key}`);
  const at = Date.now();

  // 1 · CLAIM
  let mine = false;
  const claim = await runTransaction(slot, (cur) => {
    mine = false;
    if (cur) return undefined;                 // owned, or someone else's claim — abort
    mine = true;
    return { companionId, graceId: g.id, source, game: realm, at, paidDC: cost, status: "pending" };
  });
  if (!claim.committed || !mine) {
    const v = claim.snapshot && claim.snapshot.val();
    if (v && v.status === "pending" && Date.now() - (v.at || 0) < PENDING_MS) {
      return fail("That purchase is already going through.");
    }
    return { success: true, alreadyOwned: true, key };
  }

  // 2 · PAY
  let paid = false;
  try { paid = await spendCrests(uid, cost); } catch (_) { paid = false; }
  if (!paid) {
    // release OUR claim only
    await runTransaction(slot, (cur) => (cur && cur.at === at && cur.status === "pending" ? null : undefined));
    return fail("Not enough Divine Crests.", { cost });
  }

  // 3 · SEAL
  await runTransaction(slot, (cur) => {
    if (!cur || cur.at !== at) return undefined;
    const { status, ...rest } = cur;
    return rest;
  });
  await update(ref(db, `profiles/${uid}`), {
    [`kingsGraceHistory/${histKey(at)}`]: { companionId, graceId: g.id, source, game: realm, cost, currency: "DC", at },
  });
  return { success: true, key, cost };
}

/**
 * Grant a Grace without a price — Story Mode and any future earned
 * source. Idempotent. Does NOT require the companion to be owned yet
 * (winning the story can hand you both at once).
 */
export async function grantGrace(uid, companionId, graceId, source = "story") {
  if (!looksLikeUid(uid)) return fail("No account.");
  const g = getGrace(graceId);
  if (!g) return fail("Unknown Grace.");
  if (!COMPANIONS[companionId]) return fail("Unknown companion.");
  const src = GRACE_SOURCES[source];
  if (!src || source === "legacy_radiant") return fail("Unknown source.");
  // A shop's copy is only ever BOUGHT. Granting one free would be a
  // back door around its price.
  if (Object.values(SHOP_SOURCE_OF_REALM).includes(source)) return fail("A shop Grace must be bought.");
  const key = graceKey(companionId, g.id, source);
  const at = Date.now();
  // `fresh` is decided INSIDE the transaction (the handler may run more
  // than once; the last run is the one that committed). A timestamp
  // comparison lies when two grants land in the same millisecond.
  let fresh = false;
  const tx = await runTransaction(ref(db, `${GRACE_ROOT(uid)}/${key}`), (cur) => {
    fresh = !cur;
    return cur || { companionId, graceId: g.id, source, game: src.game, at, paidDC: 0 };
  });
  return { success: true, key, fresh: !!(tx.committed && fresh) };
}

/**
 * Wear a Grace on the companion you already have equipped, or take it
 * off (graceId = null). Never changes which companion is equipped.
 */
export async function equipGrace(uid, companionId, graceId, source = null) {
  if (!looksLikeUid(uid)) return fail("Sign in first.");
  const profile = await readProfile(uid);
  if (!profile) return fail("No profile yet.");
  if (graceId === null) {
    await update(ref(db, `profiles/${uid}`), {
      equippedCompanionRadiant: null, equippedCompanionGraceSource: null,
    });
    return { success: true, cleared: true };
  }
  if (profile.equippedCompanion !== companionId) {
    return fail("Walk with this companion first — a Grace dresses the one at your side.");
  }
  const g = getGrace(graceId);
  if (!g) return fail("That Grace can't be worn.");
  const copies = gracesFor(profile, companionId).filter((c) => c.graceId === g.id);
  if (!copies.length) return fail("You don't hold that Grace.");
  const copy = copies.find((c) => c.source === source) || copies[0];
  await update(ref(db, `profiles/${uid}`), {
    equippedCompanionRadiant: g.id,
    equippedCompanionGraceSource: copy.source,
  });
  return { success: true, graceId: g.id, source: copy.source, distinctCompanions:
    new Set(ownedGraces(profile).map((r) => r.companionId)).size };
}
